import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useRouter } from '@tanstack/react-router';
import type { UseFormReturn } from 'react-hook-form';
import { BackButton } from '@/components/common/BackButton';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { FormSkeleton } from '@/components/common/FormSkeleton';
import { PageHeader } from '@/components/layout/PageHeader';
import { SurveyForm, type SurveyFormHandle } from '../components/SurveyForm/SurveyForm';
import { surveyDetailQueryOptions } from '../api/surveys.api';
import { useUpdateSurvey } from '../hooks/useUpdateSurvey';
import type { CreateSurveyFormValues } from '../types/survey.types';
import { toSurveyFormDefaultValues, toUpdateSurveyPayload } from '../utils/surveyFormMappers';

export function SurveyEditPage() {
  const { surveyId } = useParams({ from: '/_auth/feedback/surveys/$surveyId/edit' });
  const router = useRouter();
  const formRef = useRef<SurveyFormHandle>(null);
  const submittedValuesRef = useRef<CreateSurveyFormValues | null>(null);

  const {
    data: survey,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery(surveyDetailQueryOptions(surveyId));

  const defaultValues = useMemo(
    () => (survey ? toSurveyFormDefaultValues(survey) : undefined),
    [survey],
  );

  function goToDetail() {
    void router.navigate({
      to: '/feedback/surveys/$surveyId',
      params: { surveyId },
    });
  }

  const { mutate: updateSurveyMutation, isPending } = useUpdateSurvey(surveyId, () => {
    if (submittedValuesRef.current) {
      formRef.current?.reset(submittedValuesRef.current);
      submittedValuesRef.current = null;
    }
    goToDetail();
  });

  function handleSubmit(
    values: CreateSurveyFormValues,
    form: UseFormReturn<CreateSurveyFormValues>,
  ) {
    submittedValuesRef.current = form.getValues();
    updateSurveyMutation(toUpdateSurveyPayload(values));
  }

  const backButton = <BackButton onClick={goToDetail} />;

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-5">
        <PageHeader title="Edit Survey" leading={backButton} />
        <FormSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-5">
        {backButton}
        <ErrorState
          error={error}
          fallbackTitle="Could not load this survey"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      </div>
    );
  }

  if (!survey || !defaultValues) {
    return (
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-5">
        {backButton}
        <EmptyState
          title="Survey unavailable"
          description="This survey may have been deleted or the link is no longer valid."
        />
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-5">
      <PageHeader title="Edit Survey" leading={backButton} />

      <SurveyForm
        ref={formRef}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={goToDetail}
        submitLabel="Update Survey"
        pendingLabel="Updating..."
        isPending={isPending}
      />
    </div>
  );
}
